import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import { PageHeader, Panel, Button } from "../components/UIKit";
import { Save, Image as ImageIcon, RotateCcw, ExternalLink } from "lucide-react";

const CONFIG_KEY = "branding";

const DEFAULTS = {
  key: "branding",
  home_product_image_url:
    "https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=900&auto=format&fit=crop",
  home_product_image_alt: "Materasso Materassi & Materassi",
  product_name: "SmartDream",
  brand_name: "Materassi & Materassi",
};

export default function AdminBranding() {
  const { api } = useAdminAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    api(`/app-config/${CONFIG_KEY}`)
      .then((d) => setCfg(d || DEFAULTS))
      .catch(() => setCfg(DEFAULTS))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return <div className="p-8 text-white/70 text-sm">Caricamento branding…</div>;
  }

  const update = (patch) => {
    setCfg({ ...cfg, ...patch });
    setPreviewError(false);
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const res = await api(`/app-config/${CONFIG_KEY}`, {
        method: "PUT",
        data: cfg,
      });
      setCfg(res);
      setMsg("Salvato. L'immagine è aggiornata immediatamente sull'app utente.");
    } catch (e) {
      setMsg(`Errore: ${e.message || "salvataggio fallito"}`);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3500);
    }
  };

  const resetDefaults = () => {
    setCfg({ ...DEFAULTS });
    setPreviewError(false);
  };

  return (
    <div className="p-8" data-testid="admin-branding-page">
      <PageHeader
        title="Branding"
        subtitle="Immagine prodotto mostrata nella home dell'app utente"
        actions={
          <>
            <Button variant="ghost" onClick={resetDefaults} data-testid="branding-reset">
              <RotateCcw size={14} className="mr-1.5" /> Ripristina default
            </Button>
            <Button onClick={save} disabled={saving} data-testid="branding-save">
              <Save size={14} className="mr-1.5" />
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </>
        }
      />

      {msg && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            msg.startsWith("Errore")
              ? "bg-rose-500/10 border border-rose-400/30 text-rose-200"
              : "bg-[#14c2a1]/10 border border-[#14c2a1]/30 text-[#67e8d1]"
          }`}
          data-testid="branding-message"
        >
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Form */}
        <Panel title="Immagine prodotto (Home)">
          <div className="p-4 space-y-4">
            <Field
              label="URL immagine"
              testid="branding-image-url"
              value={cfg.home_product_image_url || ""}
              onChange={(v) => update({ home_product_image_url: v })}
              placeholder="https://…/materasso.png"
              helper="Suggerito: PNG/JPG/WebP con sfondo trasparente o neutro, lato lungo ≥ 800px."
            />
            <Field
              label="Testo alternativo (alt)"
              testid="branding-image-alt"
              value={cfg.home_product_image_alt || ""}
              onChange={(v) => update({ home_product_image_alt: v })}
              placeholder="Materasso Materassi & Materassi"
              helper="Descrizione per screen reader e fallback."
            />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Field
                label="Nome prodotto"
                testid="branding-product-name"
                value={cfg.product_name || ""}
                onChange={(v) => update({ product_name: v })}
                placeholder="SmartDream"
              />
              <Field
                label="Brand"
                testid="branding-brand-name"
                value={cfg.brand_name || ""}
                onChange={(v) => update({ brand_name: v })}
                placeholder="Materassi & Materassi"
              />
            </div>

            {cfg.home_product_image_url && (
              <a
                href={cfg.home_product_image_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] text-[#67e8d1] hover:text-white"
                data-testid="branding-open-url"
              >
                <ExternalLink size={12} /> Apri immagine in nuova scheda
              </a>
            )}
          </div>
        </Panel>

        {/* Preview */}
        <Panel
          title="Anteprima Home"
          right={<ImageIcon size={14} className="text-white/45" />}
        >
          <div className="p-4">
            <div
              className="relative overflow-hidden rounded-[22px] p-4 mx-auto"
              style={{
                maxWidth: 360,
                background:
                  "linear-gradient(150deg, #12b096 0%, #0a8670 45%, #083d37 100%)",
                boxShadow:
                  "0 16px 40px -16px rgba(0,168,138,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
              data-testid="branding-preview"
            >
              <div className="text-white/90 text-[11px] uppercase tracking-[0.15em] mb-1">
                {cfg.product_name || "SmartDream"}
              </div>
              <div className="text-white text-[22px] font-semibold leading-tight">
                Ciao!
              </div>
              <div className="text-white/85 text-[12px]">
                {cfg.brand_name || "Materassi & Materassi"} è pronto per monitorare il tuo riposo.
              </div>

              <div className="relative mt-4 h-[150px] flex items-center justify-center">
                {previewError ? (
                  <div className="text-rose-200 text-[12px] text-center px-4">
                    Impossibile caricare l'immagine dall'URL.
                    <br />
                    Verifica che sia pubblica e accessibile via HTTPS.
                  </div>
                ) : cfg.home_product_image_url ? (
                  <img
                    src={cfg.home_product_image_url}
                    alt={cfg.home_product_image_alt || ""}
                    onError={() => setPreviewError(true)}
                    className="w-[80%] max-w-[280px] rounded-lg object-cover drop-shadow-2xl rotate-[-6deg]"
                    style={{
                      filter: "drop-shadow(0 20px 25px rgba(0,0,0,0.35))",
                    }}
                  />
                ) : (
                  <div className="text-white/60 text-[12px]">
                    Inserisci un URL per vedere l'anteprima
                  </div>
                )}
              </div>
            </div>

            <p className="mt-4 text-white/45 text-[11px] text-center">
              L'app utente carica l'immagine al bootstrap da
              <code className="mx-1 text-white/70">/api/config/branding</code>
            </p>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, helper }) {
  return (
    <div>
      <div className="text-white/60 text-[11px] uppercase tracking-wider mb-1">
        {label}
      </div>
      <input
        type="text"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
      />
      {helper && (
        <div className="mt-1 text-white/40 text-[11px]">{helper}</div>
      )}
    </div>
  );
}
